import {
  UserUploadConfig,
  VisitsConfig,
  DeliveriesConfig,
  TicketsConfig,
  OfficeConfig,
  ScheduleConfig,
  PeopleConfig,
  InsightsConfig,
  WorkplaceDashboardConfig,
  WorkplaceManagementConfig,
  Environment,
  SetupConfig,
  FloorPlanManagerConfig,
} from '@robinpowered/dashboard-apps-common';

// Type guard to ensure NODE_ENV is a valid environment
const isValidEnvironment = (env: string | undefined): env is Environment => {
  return env === 'development' || env === 'production' || env === 'test';
};

// Helper function to get required environment variable
const getRequiredEnvVar = (name: string): string => {
  const value = process.env[name];
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
};

// Validate NODE_ENV
if (!isValidEnvironment(process.env.NODE_ENV)) {
  throw new Error(
    `Invalid NODE_ENV: ${process.env.NODE_ENV}. Must be 'development', 'production', or 'test'`
  );
}

export const userUploadConfig: UserUploadConfig = {
  env: process.env.NODE_ENV,
  userUploadApiBaseUrl: getRequiredEnvVar('REACT_APP_USER_UPLOAD_API_BASE_URL'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_USER_UPLOAD'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  dashBoardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_USER_UPLOAD'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_USER_UPLOAD'),
};

export const visitsConfig: VisitsConfig = {
  env: process.env.NODE_ENV,
  appName: getRequiredEnvVar('REACT_APP_NAME_VISITS'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_VISITS'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  robinIntercomAppId: getRequiredEnvVar('REACT_APP_ROBIN_INTERCOM_APP_ID'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_VISITS'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  badgePrintingServiceUrl: getRequiredEnvVar(
    'REACT_APP_BADGE_PRINTING_SERVICE_URL'
  ),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_VISITS'),
};

export const deliveriesConfig: DeliveriesConfig = {
  env: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_DELIVERIES'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_DELIVERIES'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_DELIVERIES'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_DELIVERIES'),
  imageServerUrl: getRequiredEnvVar('REACT_APP_IMAGE_SERVER_URL'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
};

export const ticketsConfig: TicketsConfig = {
  nodeEnv: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_TICKETS'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_TICKETS'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_TICKETS'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_TICKETS'),
  imageServerUrl: getRequiredEnvVar('REACT_APP_IMAGE_SERVER_URL'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
};

export const officeConfig: OfficeConfig = {
  env: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  atlasUrl: getRequiredEnvVar('REACT_APP_ATLAS_SERVICE_URL'),
  shareUrl: getRequiredEnvVar('REACT_APP_SHARE_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_OFFICE'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_OFFICE'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_OFFICE'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_OFFICE'),
  imageServerUrl: getRequiredEnvVar('REACT_APP_IMAGE_SERVER_URL'),
  ddApplicationId: getRequiredEnvVar('REACT_APP_DD_APPLICATION_ID'),
  ddClientToken: getRequiredEnvVar('REACT_APP_DD_CLIENT_TOKEN'),
  ddSampleRate: getRequiredEnvVar('REACT_APP_SAMPLE_RATE'),
  ddTraceSampleRate: getRequiredEnvVar('REACT_APP_DD_TRACE_SAMPLE_RATE'),
  ddSite: getRequiredEnvVar('REACT_APP_DD_SITE'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_BASE_URL'),
  intercomAppId: getRequiredEnvVar('REACT_APP_ROBIN_INTERCOM_APP_ID'),
  integrationsServiceUrl: getRequiredEnvVar(
    'REACT_APP_INTEGRATIONS_SERVICE_URL'
  ),
  apiUrl: getRequiredEnvVar('REACT_APP_ROBIN_API_URL'),
  baseApiUrl: getRequiredEnvVar('REACT_APP_ROBIN_BASE_API_URL'),
};

export const scheduleConfig: ScheduleConfig = {
  env: process.env.NODE_ENV,
  appName: getRequiredEnvVar('REACT_APP_NAME_SCHEDULE'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_SCHEDULE'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_SCHEDULE'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_SCHEDULE'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  cookiePrefix: getRequiredEnvVar('REACT_APP_COOKIE_PREFIX'),
  cookieOrigin: getRequiredEnvVar('REACT_APP_COOKIE_ORIGIN'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  imageServerUrl: getRequiredEnvVar('REACT_APP_IMAGE_SERVER_URL'),
  atlasServiceUrl: getRequiredEnvVar('REACT_APP_ATLAS_SERVICE_URL'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  apiUrl: getRequiredEnvVar('REACT_APP_ROBIN_API_URL'),
  baseApiUrl: getRequiredEnvVar('REACT_APP_ROBIN_BASE_API_URL'),
  robinShareUrl: getRequiredEnvVar('REACT_APP_ROBIN_SHARE_URL'),
  integrationsServiceUrl: getRequiredEnvVar(
    'REACT_APP_INTEGRATIONS_SERVICE_URL'
  ),
  robinServiceStatusPage: getRequiredEnvVar(
    'REACT_APP_ROBIN_SERVICE_STATUS_PAGE'
  ),
  intercomAppId: getRequiredEnvVar('REACT_APP_ROBIN_INTERCOM_APP_ID'),
  agentUrl: getRequiredEnvVar('REACT_APP_SCHEDULE_PAGE_AGENT_URL'),
};

export const peopleConfig: PeopleConfig = {
  env: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_PEOPLE'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_PEOPLE'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_PEOPLE'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_PEOPLE'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
};

export const insightsConfig: InsightsConfig = {
  env: process.env.NODE_ENV,
  appName: getRequiredEnvVar('REACT_APP_NAME_INSIGHTS'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_INSIGHTS'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  robinIntercomAppId: getRequiredEnvVar('REACT_APP_ROBIN_INTERCOM_APP_ID'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_SHELL'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_INSIGHTS'),
  insightsAiAssistantApiUrl: getRequiredEnvVar(
    'REACT_APP_INSIGHTS_AI_ASSISTANT_API_URL'
  ),
  insightsAiAssistantApiShadowUrl:
    process.env.REACT_APP_INSIGHTS_AI_ASSISTANT_API_SHADOW_URL || '',
  robinApiUrl: getRequiredEnvVar('REACT_APP_ROBIN_API_URL'),
  logrocketId: getRequiredEnvVar('REACT_APP_LOGROCKET_ID_INSIGHTS'),
  trialOrgId: getRequiredEnvVar('REACT_APP_TRIAL_ORG_ID'),
  trialOrgDefaultCampus: getRequiredEnvVar(
    'REACT_APP_TRIAL_ORG_DEFAULT_CAMPUS'
  ),
  trialOrgDefaultLocation: getRequiredEnvVar(
    'REACT_APP_TRIAL_ORG_DEFAULT_LOCATION'
  ),
  robinMarketingHomepage: getRequiredEnvVar(
    'REACT_APP_ROBIN_MARKETING_HOMEPAGE'
  ),
  robinSupportPage: getRequiredEnvVar('REACT_APP_ROBIN_SUPPORT_PAGE'),
  robinServiceStatusPage: getRequiredEnvVar(
    'REACT_APP_ROBIN_SERVICE_STATUS_PAGE'
  ),
  robinMarketingPrivacyPage: getRequiredEnvVar(
    'REACT_APP_ROBIN_MARKETING_PRIVACY_PAGE'
  ),
  robinMarketingTermsPage: getRequiredEnvVar(
    'REACT_APP_ROBIN_MARKETING_TERMS_PAGE'
  ),
  robinMarketingAcceptableUsePage: getRequiredEnvVar(
    'REACT_APP_ROBIN_MARKETING_ACCEPTABLE_USE_PAGE'
  ),
  robinApiDocumentationPage: getRequiredEnvVar(
    'REACT_APP_ROBIN_API_DOCUMENTATION_PAGE'
  ),
  region: process.env.REACT_APP_REGION || '',
  environment: getRequiredEnvVar('REACT_APP_ENVIRONMENT'),
  sentryEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
};

export const workplaceDashboardConfig: WorkplaceDashboardConfig = {
  env: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  apiUrl: getRequiredEnvVar('REACT_APP_ROBIN_API_URL'),
  atlasUrl: getRequiredEnvVar('REACT_APP_ATLAS_SERVICE_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  authServiceUrl: getRequiredEnvVar('REACT_APP_AUTH_SERVICE_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_WORKPLACE_DASHBOARD'),
  amplitudeApiKey: getRequiredEnvVar(
    'REACT_APP_AMPLITUDE_API_KEY_WORKPLACE_DASHBOARD'
  ),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_WORKPLACE_DASHBOARD'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_WORKPLACE_DASHBOARD'),
  imageServerUrl: getRequiredEnvVar('REACT_APP_IMAGE_SERVER_URL'),
  ddApplicationId: '',
  ddClientToken: '',
  ddSite: '',
  oauthClientId: getRequiredEnvVar('REACT_APP_OAUTH_CLIENT_ID'),
  featureFlagServiceUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  deviceStatusUrl: getRequiredEnvVar('REACT_APP_DEVICE_STATUS_URL'),
};

export const workplaceManagementConfig: WorkplaceManagementConfig = {
  env: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_WORKPLACE_MANAGEMENT'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_WORKPLACE_MANAGEMENT'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_WORKPLACE_MANAGEMENT'),
  amplitudeApiKey: getRequiredEnvVar(
    'REACT_APP_AMPLITUDE_API_KEY_WORKPLACE_MANAGEMENT'
  ),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  baseAtlasService: getRequiredEnvVar('REACT_APP_ATLAS_SERVICE_URL'),
};

export const setupConfig: SetupConfig = {
  env: process.env.NODE_ENV,
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  apiUrl: getRequiredEnvVar('REACT_APP_ROBIN_API_URL'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_SETUP'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  appName: getRequiredEnvVar('REACT_APP_NAME_SETUP'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_SETUP'),
  amplitudeApiKey: getRequiredEnvVar('REACT_APP_AMPLITUDE_API_KEY_SETUP'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  mapUploadUrl: getRequiredEnvVar('REACT_APP_MAP_UPLOAD_API_URL'),
  mapBulkUploadUrl: getRequiredEnvVar('REACT_APP_BULK_MAP_UPLOAD_API_URL'),
  googleMapsApiKey: getRequiredEnvVar('REACT_APP_GOOGLE_MAPS_API_KEY'),
  stripePublishableKey: getRequiredEnvVar('REACT_APP_STRIPE_PUBLISHABLE_KEY'),
};

export const floorPlanManagerConfig: FloorPlanManagerConfig = {
  env: process.env.NODE_ENV,
  robinEnv: getRequiredEnvVar('REACT_APP_ROBIN_ENV'),
  appName: getRequiredEnvVar('REACT_APP_NAME_FLOOR_PLAN_MANAGER'),
  appVersion: getRequiredEnvVar('REACT_APP_VERSION_FLOOR_PLAN_MANAGER'),
  sessionCookieName: getRequiredEnvVar('REACT_APP_COOKIE_NAME'),
  authSessionCookieName: getRequiredEnvVar('REACT_APP_AUTH_COOKIE_NAME'),
  amplitudeApiKey: getRequiredEnvVar(
    'REACT_APP_AMPLITUDE_API_KEY_FLOOR_PLAN_MANAGER'
  ),
  dashboardUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_URL'),
  gqlUrl: getRequiredEnvVar('REACT_APP_GQL_URL'),
  sentryDsn: getRequiredEnvVar('REACT_APP_SENTRY_DSN_FLOOR_PLAN_MANAGER'),
  dashboardApiUrl: getRequiredEnvVar('REACT_APP_DASHBOARD_API_URL'),
  featureFlagUrl: getRequiredEnvVar('REACT_APP_FEATURE_FLAG_URL'),
  atlasUrl: getRequiredEnvVar('REACT_APP_ATLAS_SERVICE_URL'),
};
